import { Component } from "@theme/component";
import { debounce } from "@theme/utilities";

/**
 * A custom element that manages validation for a contact form email input.
 *
 * - Ensures that the email input matches a basic email regex pattern.
 * - Provides accessible error handling using ARIA attributes.
 * - Disables or enables the submit button based on validation state.
 *
 * @extends {Component<Refs>}
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} emailInput - The email input field.
 * @property {HTMLElement} emailError - The element displaying email error messages.
 * @property {HTMLButtonElement} submitButton - The form submission button.
 */
class ContactForm extends Component {
  /** @type {Array<keyof Refs>} */
  requiredRefs = ["emailInput", "emailError", "submitButton"];

  /**
   * Regex pattern for validating email addresses.
   * Matches common email formats.
   *
   * @type {RegExp}
   * @private
   */
  #emailPattern = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

  /**
   * Tests whether the given email string matches the email regex pattern.
   *
   * @param {string} email - The email string to validate.
   * @returns {boolean} `true` if the email is valid, `false` otherwise.
   * @private
   */
  #checkValidity(email) {
    return this.#emailPattern.test(email);
  }

  /**
   * Debounced function that validates the email input field.
   *
   * - Trims the input value.
   * - Resets previous error state and accessibility attributes.
   * - If invalid, applies error styles, ARIA attributes, and disables submit.
   * - If valid, removes error styles and enables submit.
   *
   * @function
   */
  validateEmail = debounce(() => {
    const email = this.refs.emailInput.value.trim();

    this.refs.emailInput.classList.remove("contact-form__input--error");
    this.refs.emailInput.removeAttribute("aria-invalid");
    this.refs.emailInput.removeAttribute("aria-describedby");
    this.refs.emailError.classList.add("hidden");

    if (email) {
      const isValid = this.#checkValidity(email);

      if (!isValid) {
        this.refs.emailInput.classList.add("contact-form__input--error");
        this.refs.emailInput.setAttribute("aria-invalid", "true");
        this.refs.emailInput.setAttribute(
          "aria-describedby",
          "ContactForm-email-error",
        );
        this.refs.emailError.classList.remove("hidden");
        this.refs.submitButton.setAttribute("disabled", "true");
      } else {
        this.refs.submitButton.removeAttribute("disabled");
      }
    }
  }, 200);
}

customElements.define("contact-form", ContactForm);
